import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { useAuth } from "@workos-inc/authkit-react";
import { useExtendedAuth } from "@/shared/auth/AuthContext";
import { config } from "@/shared/config";
import {
  setConsent as setAnalyticsConsent,
  putConsent,
} from "@/shared/analytics";
import { bootRedditPixel } from "@/shared/reddit-pixel";

const CLARITY_ID = "uhq0r16ib2";

type ClarityWindow = Window & {
  clarity?: {
    (...args: unknown[]): void;
    q?: unknown[][];
  };
};

// Boots Microsoft Clarity lazily after the user accepts analytics. Installs
// the same queue shim the vendor snippet uses, so identify/setPage calls
// buffered by shared/analytics before the script loaded still drain through.
function bootClarity(): void {
  const w = window as ClarityWindow;
  if (w.clarity) return;
  w.clarity = function (...args: unknown[]) {
    (w.clarity!.q = w.clarity!.q || []).push(args);
  };
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
  document.head.appendChild(script);
}

// Public share surfaces must not prompt recipients for cookie consent — the
// recipient is in a contractual flow with the sender, not ProposalKit.
function isPublicShareRoute(pathname: string): boolean {
  return pathname.startsWith("/p/") || pathname.startsWith("/r/");
}

// Inline the auth-fetch shape rather than importing from app/* — site code
// cannot depend on app/*. This minimal adapter is used only to PUT the
// consent decision to /api/me/consent when the user is authenticated.
type AuthFetch = (path: string, init?: RequestInit) => Promise<Response>;

export function CookieConsentComponent() {
  const { user, getAccessToken } = useAuth();
  const { userTrialData } = useExtendedAuth();
  const location = useLocation();
  const suppressed = isPublicShareRoute(location.pathname);
  const isSampleRoute = location.pathname.startsWith("/sample/");

  const authFetch = useCallback<AuthFetch>(
    async (path, init) => {
      const token = await getAccessToken();
      return fetch(`${config.apiBaseUrl}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...init?.headers,
        },
      });
    },
    [getAccessToken],
  );

  // Hold the latest authFetch in a ref so the CookieConsent.run() callbacks
  // (which close over captured values for the component's lifetime) always
  // reach the current token-fetching function.
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;
  const isAuthedRef = useRef<boolean>(!!user);
  isAuthedRef.current = !!user;
  // Reddit Pixel advanced matching: passed at init time. If user resolves
  // after pixel boot, the email is simply absent from the match keys for
  // this session — we don't re-init.
  const userEmailRef = useRef<string | undefined>(user?.email);
  userEmailRef.current = user?.email;

  // When we push server state into vanilla-cookieconsent via acceptCategory(),
  // the library fires its own onChange. Guard against a write-amplification
  // loop: the hydration path must not trigger another PUT to the server.
  const hydratingRef = useRef(false);

  useEffect(() => {
    if (suppressed) return;
    // Share the consent cookie across proposalkit.io and app.proposalkit.io
    // so accepting on the marketing site carries through to the app. On
    // localhost we must leave domain unset — browsers drop cookies whose
    // domain doesn't match the current host.
    const host = window.location.hostname;
    const cookieDomain = host.endsWith("proposalkit.io")
      ? ".proposalkit.io"
      : undefined;

    CookieConsent.run({
      autoClearCookies: true,
      hideFromBots: false,
      ...(cookieDomain ? { cookie: { domain: cookieDomain } } : {}),

      onConsent: () => {
        const preferences = CookieConsent.getUserPreferences();
        const granted = preferences.acceptedCategories.includes("analytics");
        const value = granted ? "granted" : "denied";
        if (granted) {
          bootClarity();
          bootRedditPixel(userEmailRef.current);
        }
        setAnalyticsConsent(value);
        if (!hydratingRef.current && isAuthedRef.current) {
          void putConsent(authFetchRef.current, value);
        }
      },

      onChange: ({ changedCategories }) => {
        if (!changedCategories.includes("analytics")) return;
        const preferences = CookieConsent.getUserPreferences();
        const granted = preferences.acceptedCategories.includes("analytics");
        const value = granted ? "granted" : "denied";
        if (granted) {
          bootClarity();
          bootRedditPixel(userEmailRef.current);
        }
        setAnalyticsConsent(value);
        if (!hydratingRef.current && isAuthedRef.current) {
          void putConsent(authFetchRef.current, value);
        }
      },

      guiOptions: {
        consentModal: {
          layout: "box inline",
          position: "bottom right",
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: "box",
          position: "right",
          equalWeightButtons: true,
          flipButtons: false,
        },
      },

      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: { enabled: false, readOnly: false },
      },

      language: {
        default: "en",
        translations: {
          en: {
            consentModal: {
              title: "Hello! It's cookie time 🍪",
              description: isSampleRoute
                ? 'We use cookies to analyze site traffic and improve your experience. By clicking "Accept all," you consent to this. <br /><br /><em>Heads up: this banner only shows on ProposalKit\'s own pages. Proposals you send to your clients stay clean — no cookie banner, no third-party trackers.</em>'
                : 'We use cookies to analyze site traffic and improve your experience. By clicking "Accept all," you consent to this.',
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage preferences",
            },
            preferencesModal: {
              title: "Cookie Preferences Center",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save preferences",
              closeIconLabel: "Close modal",
              serviceCounterLabel: "Service|Services",
              sections: [
                {
                  title: "Cookie Usage",
                  description:
                    "We use cookies to ensure our website functions properly and to collect analytics data. Consent to these cookies will optimize your user experience.",
                },
                {
                  title:
                    'Strictly Necessary Cookies <span class="pm__badge">Always Enabled</span>',
                  description:
                    "These are essential for the website to function and cannot be switched off. They are usually only set in response to actions made by you which amount to a request for services.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Analytics Cookies",
                  description:
                    "We use analytics cookies to collect information about how you interact with our website. This helps us to improve our website and its content.",
                  linkedCategory: "analytics",
                },
                {
                  title: "More information",
                  description:
                    "For any questions regarding our cookie policy and your choices, please contact us or review our Privacy Policy.",
                },
              ],
            },
          },
        },
      },
    });
    // isSampleRoute is read once at mount to vary banner copy. Re-running
    // CookieConsent.run on later route changes would re-init the library.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppressed]);

  // Server-side consent is the source of truth for authenticated users. Once
  // userTrialData arrives, reconcile: if the server has a choice, push it
  // down to the cookie (so the banner doesn't re-prompt on this device); if
  // the server has none but this device already answered on the marketing
  // site, migrate the cookie's choice up to the server.
  useEffect(() => {
    if (suppressed) return;
    if (!user || !userTrialData) return;

    const serverConsent = userTrialData.analytics_consent;

    if (serverConsent) {
      hydratingRef.current = true;
      try {
        if (serverConsent === "granted") {
          CookieConsent.acceptCategory(["analytics", "necessary"]);
        } else {
          CookieConsent.acceptCategory("necessary");
        }
        setAnalyticsConsent(serverConsent);
      } finally {
        hydratingRef.current = false;
      }
      return;
    }

    if (CookieConsent.validConsent()) {
      const granted = CookieConsent.acceptedCategory("analytics");
      const value = granted ? "granted" : "denied";
      setAnalyticsConsent(value);
      void putConsent(authFetch, value);
    }
  }, [user, userTrialData, authFetch, suppressed]);

  return null;
}
